/**
 * Trivia engine — the variant-agnostic gameplay core.
 *
 * Every visual variant of trivia (Reading Room, the upcoming variants the
 * variant agents are about to build) consumes this engine. Variants supply
 * the renderer; the engine owns:
 *  - phase state machine (intro → question → reveal → done)
 *  - per-trial scoring + time bonus
 *  - GameSession integration (telemetry emits)
 *  - difficulty integration (snapshot emit on first move)
 *  - reveal-hold timing (the deliberate pause before Next becomes available)
 *  - category rotation
 *  - bank selection via `pickRoundQuestions`
 *
 * This file is variant-agnostic: no JSX, no CSS imports, no motion library,
 * no variant-specific palette. Just state + actions. Variants call
 * `useTriviaEngine` and render against the returned state.
 *
 * Why a hook (not a class): the engine is React-shaped — state-driven, async
 * timers, side-effecting telemetry. A hook is the right shape for that, and
 * variants live inside React anyway.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { prefersReducedMotion, timings } from '@/juice';
import type { DifficultyEngine, GameSession } from '@rendever/games-sdk';

import { dailyTriviaSeed, pickRoundQuestions, type TriviaQuestion } from './bank/index';
import type { TriviaCategorySlug } from './categoriesSlug';

export type TriviaPhase = 'intro' | 'question' | 'reveal' | 'done';
export type TriviaMode = 'daily' | 'practice';

export interface TriviaTrialResult {
  questionId: string;
  category: string;
  chosenIndex: number;
  correctIndex: number;
  reactionTimeMs: number;
  correct: boolean;
  scoreAwarded: number;
}

export interface TriviaEngineConfig {
  session: GameSession;
  difficulty: DifficultyEngine;
  initialCategorySlug: TriviaCategorySlug;
  mode?: TriviaMode;
  /** Test-only clock injection for daily question selection. */
  now?: Date;
  /** How many questions per round. Default 5. */
  questionsPerRound?: number;
  /** Pick the next category to rotate to. Variants control this. */
  nextCategorySlug: (current: TriviaCategorySlug) => TriviaCategorySlug;
  /** Optional callbacks invoked at variant-visible moments. */
  onAnswered?: (trial: TriviaTrialResult) => void;
}

export interface TriviaEngineState {
  // Identity
  mode: TriviaMode;
  categorySlug: TriviaCategorySlug;
  questions: ReadonlyArray<TriviaQuestion>;
  questionsPerRound: number;
  // Phase
  phase: TriviaPhase;
  questionIndex: number;
  /** Current question, or undefined when phase is 'done'. */
  currentQuestion: TriviaQuestion | undefined;
  isLastQuestion: boolean;
  // Selection
  chosenIndex: number | null;
  /** True after the deliberate reveal-hold has elapsed and Next is permitted. */
  revealReady: boolean;
  // Scoring
  results: ReadonlyArray<TriviaTrialResult>;
  score: number;
  streak: number;
  correctSoFar: number;
  // Re-mount key for variant renderers (cards remount on this number changing).
  cardKey: number;
}

export interface TriviaEngineActions {
  start: () => void;
  answer: (index: number) => void;
  next: () => void;
  anotherRound: () => void;
  end: () => Promise<void>;
}

export interface TriviaEngineHandle {
  state: TriviaEngineState;
  actions: TriviaEngineActions;
}

const DEFAULT_QUESTIONS_PER_ROUND = 5;
const POINTS_PER_CORRECT = 100;
const TIME_BONUS_MAX = 50;
const FAST_THRESHOLD_MS = 1500;
const SLOW_THRESHOLD_MS = 8000;
/** Held-frame before Next becomes available — the juice. */
const REVEAL_HOLD_MS = 400;

export function pickTriviaQuestionsForMode(
  mode: TriviaMode,
  slug: TriviaCategorySlug,
  n: number,
  now: Date = new Date(),
): TriviaQuestion[] {
  if (mode === 'daily') {
    return pickRoundQuestions(slug, n, {
      seed: dailyTriviaSeed(slug, now),
    });
  }
  return pickRoundQuestions(slug, n);
}

/**
 * Variant-agnostic trivia state engine. Returns `{ state, actions }`.
 * Variants render against `state` and call `actions` from buttons.
 */
export function useTriviaEngine(config: TriviaEngineConfig): TriviaEngineHandle {
  const {
    session,
    difficulty,
    initialCategorySlug,
    mode = 'daily',
    now,
    questionsPerRound = DEFAULT_QUESTIONS_PER_ROUND,
    nextCategorySlug,
    onAnswered,
  } = config;

  const [categorySlug, setCategorySlug] = useState<TriviaCategorySlug>(initialCategorySlug);
  const [questions, setQuestions] = useState<TriviaQuestion[]>(() =>
    pickTriviaQuestionsForMode(mode, initialCategorySlug, questionsPerRound, now),
  );
  const [phase, setPhase] = useState<TriviaPhase>('intro');
  const [questionIndex, setQuestionIndex] = useState(0);
  const [chosenIndex, setChosenIndex] = useState<number | null>(null);
  const [results, setResults] = useState<TriviaTrialResult[]>([]);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [cardKey, setCardKey] = useState(0);
  const [revealReady, setRevealReady] = useState(false);

  const promptShownAt = useRef<number | null>(null);
  const started = useRef(false);
  const difficultyLogged = useRef(false);

  const currentQuestion = questions[questionIndex];
  const isLastQuestion = questionIndex >= questionsPerRound - 1;
  const correctSoFar = useMemo(() => results.filter((r) => r.correct).length, [results]);

  // Difficulty snapshot — emit once after first start.
  useEffect(() => {
    if (difficultyLogged.current) return;
    if (!started.current) return;
    difficultyLogged.current = true;
    const snap = difficulty.snapshot();
    void session.emit('difficulty_snapshot', {
      axes: snap.axes,
      successRate: snap.successRate,
      resultCount: snap.resultCount,
    });
  }, [phase, difficulty, session]);

  // Prompt shown — emit on every question entrance.
  useEffect(() => {
    if (phase !== 'question' || !currentQuestion) return;
    promptShownAt.current = performance.now();
    void session.emit('prompt_shown', {
      questionId: currentQuestion.id,
      category: currentQuestion.category,
      questionIndex,
    });
  }, [phase, currentQuestion, questionIndex, session]);

  const start = useCallback((): void => {
    if (!started.current) {
      started.current = true;
      void session.start();
    }
    setCardKey((k) => k + 1);
    setPhase('question');
  }, [session]);

  const answer = useCallback(
    (index: number): void => {
      if (phase !== 'question') return;
      if (!currentQuestion) return;

      const now = performance.now();
      const reactionTimeMs =
        promptShownAt.current === null ? 0 : Math.max(0, Math.round(now - promptShownAt.current));
      const correct = index === currentQuestion.correctIndex;

      setChosenIndex(index);
      setRevealReady(false);

      // Brief "world holds its breath" pause before the reveal lands.
      const revealDelay = prefersReducedMotion() ? 0 : timings.fast;
      window.setTimeout(() => {
        const timeBonus = correct ? computeTimeBonus(reactionTimeMs) : 0;
        const award = correct ? POINTS_PER_CORRECT + timeBonus : 0;
        const trial: TriviaTrialResult = {
          questionId: currentQuestion.id,
          category: currentQuestion.category,
          chosenIndex: index,
          correctIndex: currentQuestion.correctIndex,
          reactionTimeMs,
          correct,
          scoreAwarded: award,
        };

        void session.emit(
          correct ? 'correct' : 'incorrect',
          {
            questionId: currentQuestion.id,
            chosenIndex: index,
            correctIndex: currentQuestion.correctIndex,
            category: currentQuestion.category,
          },
          reactionTimeMs,
        );

        if (correct) setStreak((s) => s + 1);
        else setStreak(0);

        setResults((prev) => [...prev, trial]);
        setScore((s) => s + award);
        setPhase('reveal');

        onAnswered?.(trial);

        // Hold the reveal before Next becomes available.
        const holdMs = prefersReducedMotion() ? 0 : REVEAL_HOLD_MS;
        window.setTimeout(() => setRevealReady(true), holdMs);
      }, revealDelay);
    },
    [phase, currentQuestion, session, onAnswered],
  );

  const next = useCallback((): void => {
    if (isLastQuestion) {
      setPhase('done');
    } else {
      setQuestionIndex((i) => i + 1);
      setChosenIndex(null);
      setCardKey((k) => k + 1);
      setRevealReady(false);
      setPhase('question');
    }
  }, [isLastQuestion]);

  const anotherRound = useCallback((): void => {
    const nextSlug = nextCategorySlug(categorySlug);
    setCategorySlug(nextSlug);
    setQuestions(pickTriviaQuestionsForMode(mode, nextSlug, questionsPerRound, now));
    setQuestionIndex(0);
    setChosenIndex(null);
    setResults([]);
    setScore(0);
    setStreak(0);
    setCardKey((k) => k + 1);
    setRevealReady(false);
    void session.emit('round_started', {
      category: nextSlug,
      sequenceWithinSession: 'continuation',
    });
    setPhase('intro');
  }, [categorySlug, questionsPerRound, nextCategorySlug, session, mode, now]);

  const end = useCallback(async (): Promise<void> => {
    const total = results.length || 1;
    const accuracy = correctSoFar / total;
    await session.end({
      finalScore: score,
      accuracy,
      completed: true,
    });
  }, [results.length, correctSoFar, score, session]);

  const state: TriviaEngineState = {
    mode,
    categorySlug,
    questions,
    questionsPerRound,
    phase,
    questionIndex,
    currentQuestion,
    isLastQuestion,
    chosenIndex,
    revealReady,
    results,
    score,
    streak,
    correctSoFar,
    cardKey,
  };

  const actions: TriviaEngineActions = { start, answer, next, anotherRound, end };

  return { state, actions };
}

// ---------------------------------------------------------------------------
// Helpers — usable by variants for displaying derived stats.
// ---------------------------------------------------------------------------

function computeTimeBonus(reactionTimeMs: number): number {
  if (reactionTimeMs <= FAST_THRESHOLD_MS) return TIME_BONUS_MAX;
  if (reactionTimeMs >= SLOW_THRESHOLD_MS) return 0;
  const range = SLOW_THRESHOLD_MS - FAST_THRESHOLD_MS;
  const into = reactionTimeMs - FAST_THRESHOLD_MS;
  const remaining = 1 - into / range;
  return Math.max(0, Math.round(TIME_BONUS_MAX * remaining));
}

/** Turn raw ms into plain warm English. */
export function humanizeReactionTime(ms: number): string {
  if (ms <= 0) return '—';
  if (ms < 1500) return 'About a second';
  if (ms < 2500) return 'Around 2 seconds';
  if (ms < 3500) return 'Around 3 seconds';
  if (ms < 5000) return 'A few seconds';
  if (ms < 8000) return 'Unhurried';
  return 'Took your time — good';
}

/** Average reaction time across results, in ms (rounded). */
export function averageReactionTime(results: ReadonlyArray<TriviaTrialResult>): number {
  if (results.length === 0) return 0;
  return Math.round(results.reduce((a, r) => a + r.reactionTimeMs, 0) / results.length);
}
